import type { BrowserBackend } from "../backend/contracts";
import type { LocalAppManifest } from "./contracts";

export const WORKSPACE_PACKAGE_VERSION = 1 as const;

type WorkspacePackageBody = {
  kind: "browser-local-workspace-package";
  packageVersion: typeof WORKSPACE_PACKAGE_VERSION;
  createdAt: string;
  app: {
    id: string;
    version: string;
    dataSchemaVersion: number;
    storageNamespace: string;
  };
  payload: {
    snapshotBase64: string;
    snapshotBytes: number;
  };
};

export type PortableWorkspacePackage = WorkspacePackageBody & {
  checksum: string;
};

export class WorkspacePackageError extends Error {
  constructor(
    public readonly code: "invalid_package" | "app_mismatch" | "unsupported_schema",
    message: string,
  ) {
    super(message);
    this.name = "WorkspacePackageError";
  }
}

export async function exportWorkspacePackage(
  backend: Pick<BrowserBackend, "snapshot">,
  app: Pick<LocalAppManifest, "id" | "version" | "dataSchemaVersion" | "storageNamespace">,
): Promise<Uint8Array> {
  const snapshot = await backend.snapshot();
  const body: WorkspacePackageBody = {
    kind: "browser-local-workspace-package",
    packageVersion: WORKSPACE_PACKAGE_VERSION,
    createdAt: new Date().toISOString(),
    app: {
      id: app.id,
      version: app.version,
      dataSchemaVersion: app.dataSchemaVersion,
      storageNamespace: app.storageNamespace,
    },
    payload: {
      snapshotBase64: encodeBase64(snapshot),
      snapshotBytes: snapshot.byteLength,
    },
  };
  const packageValue: PortableWorkspacePackage = {
    ...body,
    checksum: await checksum(body),
  };
  return new TextEncoder().encode(JSON.stringify(packageValue));
}

export async function restoreWorkspacePackage(
  backend: Pick<BrowserBackend, "restore">,
  bytes: Uint8Array,
  expectedApp: Pick<LocalAppManifest, "id" | "dataSchemaVersion">,
): Promise<PortableWorkspacePackage> {
  const value = parsePackage(bytes);
  if (value.app.id !== expectedApp.id) {
    throw new WorkspacePackageError("app_mismatch", `Package belongs to ${value.app.id}, not ${expectedApp.id}.`);
  }
  if (value.app.dataSchemaVersion > expectedApp.dataSchemaVersion) {
    throw new WorkspacePackageError("unsupported_schema", `Package schema v${value.app.dataSchemaVersion} is newer than supported v${expectedApp.dataSchemaVersion}.`);
  }
  const body: WorkspacePackageBody = {
    kind: value.kind,
    packageVersion: value.packageVersion,
    createdAt: value.createdAt,
    app: value.app,
    payload: value.payload,
  };
  if (value.checksum !== await checksum(body)) {
    throw new WorkspacePackageError("invalid_package", "Workspace package checksum does not match.");
  }
  const snapshot = decodeBase64(value.payload.snapshotBase64);
  if (snapshot.byteLength !== value.payload.snapshotBytes) {
    throw new WorkspacePackageError("invalid_package", "Workspace package snapshot size does not match.");
  }

  // All envelope, checksum and size validation completes before backend.restore mutates state.
  await backend.restore(snapshot);
  return value;
}

function parsePackage(bytes: Uint8Array): PortableWorkspacePackage {
  let value: unknown;
  try {
    value = JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    throw new WorkspacePackageError("invalid_package", "Workspace package is not valid JSON.");
  }
  if (!value || typeof value !== "object") {
    throw new WorkspacePackageError("invalid_package", "Workspace package must be an object.");
  }
  const candidate = value as Partial<PortableWorkspacePackage>;
  if (candidate.kind !== "browser-local-workspace-package" || candidate.packageVersion !== WORKSPACE_PACKAGE_VERSION) {
    throw new WorkspacePackageError("invalid_package", "Workspace package version is not supported.");
  }
  if (typeof candidate.checksum !== "string" || !candidate.app || !candidate.payload || typeof candidate.createdAt !== "string") {
    throw new WorkspacePackageError("invalid_package", "Workspace package envelope is incomplete.");
  }
  if (
    typeof candidate.app.id !== "string" ||
    typeof candidate.app.version !== "string" ||
    !Number.isInteger(candidate.app.dataSchemaVersion) ||
    typeof candidate.app.storageNamespace !== "string"
  ) {
    throw new WorkspacePackageError("invalid_package", "Workspace package app metadata is invalid.");
  }
  if (
    typeof candidate.payload.snapshotBase64 !== "string" ||
    !Number.isInteger(candidate.payload.snapshotBytes) ||
    candidate.payload.snapshotBytes < 0
  ) {
    throw new WorkspacePackageError("invalid_package", "Workspace package payload metadata is invalid.");
  }
  return candidate as PortableWorkspacePackage;
}

async function checksum(value: WorkspacePackageBody): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(JSON.stringify(value)));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

function encodeBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return btoa(binary);
}

function decodeBase64(value: string): Uint8Array {
  try {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return bytes;
  } catch {
    throw new WorkspacePackageError("invalid_package", "Workspace package payload is not valid base64.");
  }
}
